import React from "react";
import { useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { selectUser } from "../../../store/slices/authentication";
import BlockedFeatureBanner from "../../shared/BlockedFeatureBanner";
import SharedHorizontalTabs from "../../shared/HorizontalTabs";
import SectionMagazine from "./SectionMagazine";
import useScrollToHash from "./useScrollToHash";
import { SECTIONS } from "./util";

// The Blog (code name "Footprint") — a read-only "family magazine", shown as
// the Blog view inside the Bulletin (Sam, 2026-09-26). Four sections as sub-tabs,
// each laid out in full so nothing needs opening. The Bulletin already owns
// "tab", so the sections use "section": a single piece is shared as
// /bulletin?view=blog&section=<section>#a-<slug>, the same link its index tile
// uses. Old /footprint?tab=<section> links are sent here by FootprintRedirect.
// Everything renders from clean content blocks via our own components — no raw
// HTML anywhere.
function Footprint() {
  const param = "section";
  const user = useSelector(selectUser);
  const individualId = user?.externalId ? parseInt(user.externalId) : null;
  const [searchParams, setSearchParams] = useSearchParams();
  const { currentTabIndex } = SharedHorizontalTabs.useGetTabIndex(SECTIONS, param);
  const section = SECTIONS[currentTabIndex].key;

  // A shared link names a piece as #a-<slug>. Resolve it once THIS section has
  // rendered — the browser's own attempt happens too early to find anything.
  useScrollToHash(section);

  const handleTabChange = (index) => {
    const next = new URLSearchParams(searchParams);
    next.set(param, SECTIONS[index].key);
    setSearchParams(next, { replace: true });
  };

  // The route enforces this too -- the banner is what tells the member WHY the
  // section is empty instead of leaving them staring at a blank page.
  return (
    <BlockedFeatureBanner
      featureKey="is_footprint_blocked"
      featureName="The Blog"
      individualId={individualId}
      mode="full"
    >
    <div className="w-full">
      {/* The PAGE is full width and each piece constrains its own content.
          The old shape put a max-width on the page itself, which trapped the
          landmark bands inside a 1000px box — they are meant to run edge to
          edge, the way they did on the original site. */}
      <div className="w-full pb-[28px]">
        {/* Centred to match the index tiles below. The masthead used to be
            left-aligned over centred content, which read as two different
            pages stacked. */}
        <div className="max-w-[1080px] mx-auto px-[24px] text-center pt-[6px]">
          <p className="font-blairBody text-[13px] tracking-[0.14em] uppercase
                        text-gray-500 dark:text-gray-400 mt-[10px]">
            Stories, history, and the places that made us
          </p>
          <div className="mx-auto mt-[14px] h-[3px] w-[54px] bg-blairGold" aria-hidden="true" />
        </div>

        <div className="max-w-[1080px] mx-auto px-[24px] mt-[24px] mb-[26px]">
          <SharedHorizontalTabs.Component
            tabs={SECTIONS.map((t) => t.label)}
            initialIndex={currentTabIndex}
            onChange={handleTabChange}
            className="justify-center flex-wrap gap-x-[26px] border-b border-blairGold/30 pb-[9px]"
          />
        </div>
        <SectionMagazine section={section} />
      </div>
    </div>
    </BlockedFeatureBanner>
  );
}

export default Footprint;
